import { ReactNode, useContext, useEffect, useMemo } from 'react';
import { SmartSendItemsCache } from '../../lib/cache/SmartSendItemsCache';
import { chunk } from '../../lib/helpers/ArrayHelper';
import { getIds, getIdsByState, isQuotableItem } from '../../lib/context/ContextFunctions';
import {
  AddNewItemsAction,
  ApplyStateToItemsAction,
  SmartSendKeyedState,
  UpdateItemsCacheAction
} from '../../lib/context/SmartSendContextModels';
import { GetClientSideFailedValidation } from '../../lib/context/GetClientSideFailedValidation';
import { IsReturnCollection, IsSmartSend } from '../../lib/helpers/SmartSendSummaryGenerator';
import { ApplierOption } from '../../lib/helpers/ApplierOption';
import { SmartSendContext } from '../../lib/context/SmartSendContext';
import { ItemExtrasAutoApplier } from '../../lib/helpers/ItemExtrasAutoApplier';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import useQuoteHelper from '../../lib/helpers/useQuoteHelper';

const SmartSendItemStateObserver: React.FC<{ children?: ReactNode }> = (props) => {
  const { quoteHelper } = useQuoteHelper();
  const ctx = useContext(SmartSendContext);
  const {
    bulkUpdateItem,
    bulkUpdateStatus,
    createReturnCollectionForOrder,
    createReturnCollectionForSmartSendItem,
    getItemsByIds
  } = useSmartSendApi();
  const requireQuotes = useMemo(() => getIdsByState(['QUOTE'], SmartSendItemsCache), [ctx.state]);
  const initial = useMemo(() => getIdsByState(['INITIAL', 'NEW'], SmartSendItemsCache), [ctx.state]);
  const requireReturns = useMemo(() => getIdsByState(['RETURN'], SmartSendItemsCache), [ctx.state]);
  const requireSaving = useMemo(() => getIdsByState(['SAVE'], SmartSendItemsCache), [ctx.state]);

  const dispatchChange = (state: string, ids: string[]) => {
    ctx.dispatch(new ApplyStateToItemsAction(ids, state));
  };

  const updateItemsCache = (smartSendItemStates: SmartSendKeyedState[]) => {
    ctx.dispatch(new UpdateItemsCacheAction(smartSendItemStates));
  };

  const changeToStateAndApply = (changeToStatus: string, stringIds: string): string[] => {
    if (!stringIds) {
      return [];
    }
    const ids = getIds(stringIds);
    if (ids.length > 0) {
      dispatchChange(changeToStatus, ids);
    }
    return ids;
  };

  // This handles quoting.
  useEffect(() => {
    if (!requireQuotes) {
      return;
    }

    let ids = changeToStateAndApply('QUOTING', requireQuotes);

    // Batch 'State Update' the ones we can clientside validate.
    const failedClientSideValidated = GetClientSideFailedValidation(ids);
    if (failedClientSideValidated.length > 0) {
      ids = ids.filter((x) => !failedClientSideValidated.map((b) => b.key).includes(x));
      updateItemsCache(failedClientSideValidated);
    }

    // Remove Unquotable items and set to IDLE.
    const unquotable = ids
      .map((id) => {
        return { key: id, value: SmartSendItemsCache.find((x) => x.Id === id) };
      })
      .filter((x) => !!x.value && !isQuotableItem(x.value));
    if (unquotable.length > 0) {
      ids = ids.filter((x) => !unquotable.map((b) => b.key).includes(x));
      dispatchChange(
        'IDLE',
        unquotable.map((x) => x.key)
      );
    }

    quoteHelper(
      ids.map((id) => SmartSendItemsCache.find((x) => x.Id === id)).filter((x) => !!x),
      () => {},
      () => {},
      (x) => {
        console.log(`Batch ${x.length} Done`);
        updateItemsCache(x);
      },
      (x) => {
        console.log(`Total ${x.length} Done`);
      }
    );
  }, [requireQuotes]);

  // This handles the items which need saving.
  useEffect(() => {
    if (!requireSaving) {
      return;
    }

    const ids = changeToStateAndApply('SAVING', requireSaving);

    // Get all the items which can be updated
    const updatable = ids
      .map((id) => {
        return { key: id, value: SmartSendItemsCache.filter((x) => x.Id === id).map(ItemExtrasAutoApplier)[0] };
      })
      .filter((x) => !!x.value)
      .filter((x) => {
        const c = SmartSendItemsCache.find((i) => i.Id === x.key)?.State;
        return c && c.unsaved?.length > 0;
      });

    const noChanges = ids.filter((x) => !updatable.map((u) => u.key).includes(x));
    if (noChanges.length > 0) {
      dispatchChange('IDLE', noChanges);
    }

    if (updatable.length > 0) {
      dispatchChange(
        'QUOTE',
        updatable.map((x) => x.key)
      );

      // Separate the status changes and batch them
      const justStatusChange = updatable.filter((x) => {
        const c = SmartSendItemsCache.find((i) => i.Id === x.key)?.State;
        return c && c.unsaved.length === 1 && c.unsaved[0] === 'Status';
      });
      if (justStatusChange.length) {
        const statuses: { [key: string]: string[] } = {};
        justStatusChange.forEach((x) => {
          const c = SmartSendItemsCache.find((i) => i.Id === x.key);
          statuses[c.Status] = statuses[c.Status] || [];
          statuses[c.Status].push(c.Id);
        });
        for (const s in statuses) {
          bulkUpdateStatus(statuses[s], s as any);
        }
      }

      // Update the full item
      const others = updatable.filter((x) => !justStatusChange.map((j) => j.key).includes(x.key));
      chunk(others, SettingsCache.settings.Limits.UpdateBatchSize).forEach((chunk) => {
        bulkUpdateItem(
          chunk.map((x) => {
            return { Item: x.value, Fields: x.value.State.unsaved as ApplierOption[] };
          })
        );
      });
    }
  }, [requireSaving]);

  // This handles the items which need returning.
  useEffect(() => {
    if (!requireReturns) {
      return;
    }

    const ids = changeToStateAndApply('IDLE', requireReturns);
    if (!ids || ids.length === 0) {
      return;
    }

    const promises = ids.map(async (id) => {
      const item = SmartSendItemsCache.find((x) => x.Id === id);
      if (!item || IsReturnCollection(item)) {
        return null;
      }
      if (IsSmartSend(item)) {
        return await createReturnCollectionForSmartSendItem(id);
      } else {
        return await createReturnCollectionForOrder(id);
      }
    });

    Promise.all(promises).then((r) => {
      const newIds = r.filter((x) => x && x.success).map((x) => x?.result.Item.Id);
      getItemsByIds(newIds).then((r) => {
        if (r.success) {
          ctx.dispatch(new AddNewItemsAction(r.result.Items));
        }
      });
    });
  }, [requireReturns]);

  // This handles the items which have been queued for quotes and those loaded initially
  useEffect(() => {
    if (!initial) {
      return;
    }

    let ids = getIds(initial);

    // Batch State update the one we can clientside validate.
    const batchUpdated = GetClientSideFailedValidation(ids);
    if (batchUpdated.length > 0) {
      ctx.dispatch(new UpdateItemsCacheAction(batchUpdated));
    }

    // Only Quote the ones we need API calls for, and max out at 25 concurrent quotes
    ids = ids.filter((x) => !batchUpdated.map((y) => y.key).includes(x));
    dispatchChange('QUOTE', ids);
  }, [initial]);

  return <>{props.children}</>;
};

export { SmartSendItemStateObserver };
